import React, { useState } from "react";
import {
  FaCar,
  FaEdit,
  FaCheck,
  FaTimes,
  FaUpload,
  FaSpinner,
} from "react-icons/fa";
import API from "../config/config";
import axios from "axios";
import DocumentStatusEditor from "./DocumentStatusEditor";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { DocumentImage } from "./DocumentImageViewer";

const DL_DUPLICATE_MESSAGE = "dl number is already used";

const isDuplicateDlMessage = (message) => {
  const normalized = String(message || "").toLowerCase();
  return (
    normalized === DL_DUPLICATE_MESSAGE ||
    normalized.includes("already associated with another driver") ||
    normalized.includes("already onboarded with another driver")
  );
};

const DrivingLicenseDetails = ({
  drivingLicense,
  driverId,
  editable,
  editableDetails,
  editableStatus,
  currentUserRole,
  showIfEmpty = false,
  onUpdated,
}) => {
  const token = localStorage.getItem("adminToken");
  const detailsEditable = editableDetails !== undefined ? editableDetails : editable;
  const statusEditable = editableStatus !== undefined ? editableStatus : editable;

  const [editField, setEditField] = useState("");
  const [uploadingSide, setUploadingSide] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [dlNumberError, setDlNumberError] = useState("");

  const [formData, setFormData] = useState({
    dateOfBirth: drivingLicense?.dateOfBirth || "",
    dateOfIssue: drivingLicense?.dateOfIssue || "",
    dateOfExpire: drivingLicense?.dateOfExpire || "",
    dlNumber: drivingLicense?.dlNumber || "",
    status: drivingLicense?.status,
    statusReason: drivingLicense?.statusReason || "",
    dlfront: drivingLicense?.dlfront,
    dlback: drivingLicense?.dlback,
    parivahanDoc: drivingLicense?.parivahanDoc,
  });

  const noDLData =
    !drivingLicense ||
    (!drivingLicense.dlNumber &&
      !drivingLicense.dlfront &&
      !drivingLicense.dlback &&
      !drivingLicense.dateOfBirth &&
      !drivingLicense.dateOfIssue &&
      !drivingLicense.dateOfExpire);

  // Show component if showIfEmpty is true, even when empty
  if (!showIfEmpty && noDLData) return null;
  
  const parseFlexibleDate = (value) => {
    if (!value) return null;

    const raw = String(value).trim();

    const ddmmyyyyMatch = raw.match(/^(\d{2})-(\d{2})-(\d{4})(?:[-T ].*)?$/);
    if (ddmmyyyyMatch) {
      const day = Number(ddmmyyyyMatch[1]);
      const month = Number(ddmmyyyyMatch[2]);
      const year = Number(ddmmyyyyMatch[3]);
      const parsed = new Date(Date.UTC(year, month - 1, day));
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const yyyymmddMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/);
    if (yyyymmddMatch) {
      const year = Number(yyyymmddMatch[1]);
      const month = Number(yyyymmddMatch[2]);
      const day = Number(yyyymmddMatch[3]);
      const parsed = new Date(Date.UTC(year, month - 1, day));
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatDisplayDate = (isoDateStr) => {
    if (!isoDateStr) return "N/A";
    const date = parseFlexibleDate(isoDateStr);
    return date ? date.toLocaleDateString("en-IN") : "N/A";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "dlNumber") {
      setDlNumberError("");
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date, fieldName) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: date && !Number.isNaN(date.getTime()) ? date.toISOString() : "",
    }));
  };

  const isDlNumberAlreadyUsed = async (trimmedDlNumber) => {
    const response = await axios.get(API.ADMIN.DUPLICATE_DL_CHECK, {
      params: {
        dlNumber: trimmedDlNumber,
        excludeDriverId: driverId,
      },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data?.exists === true;
  };

  const getUploadType = (side) => {
    if (side === "dlfront") return "dl_front";
    if (side === "dlback") return "dl_back";
    if (side === "parivahanDoc") return "parivahan_doc";
    return "dl_back";
  };

  const handleFileChange = async (e, side) => {
    const file = e.target.files[0];
    if (!file) return;

    // Parivahan Doc: allow only image or PDF (no xlsx, csv)
    if (side === "parivahanDoc") {
      const isImage = (file.type || "").startsWith("image/");
      const isPdf = (file.type || "").toLowerCase() === "application/pdf";
      if (!isImage && !isPdf) {
        alert("Parivahan Doc accepts only images or PDF files.");
        return;
      }
    }

    setUploadingSide(side);
    try {
      const formdata = new FormData();
      formdata.append("documentFor", "Driver");
      formdata.append("type", getUploadType(side));
      formdata.append("file", file, file.name);
      formdata.append("ownerId", driverId);

      const response = await fetch(API.ADMIN.ADMINDOCUMENT_UPLOAD, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + token,
        },
        body: formdata,
      });

      const data = await response.json();
      const uploadedUrl = data.document?.documentUrl;
      if (!uploadedUrl) throw new Error("Upload failed");

      // If DL record doesn't exist, create it using ADMIN_VERIFY_DRIVING_LICENSE
      if (!drivingLicense || !drivingLicense._id) {
        const payload = {
          ownerId: driverId,
          dlNumber: formData.dlNumber || "",
          dateOfBirth: formData.dateOfBirth || "",
          dateOfIssue: formData.dateOfIssue || "",
          dateOfExpire: formData.dateOfExpire || "",
          dlfront: side === "dlfront" ? uploadedUrl : formData.dlfront || "",
          dlback: side === "dlback" ? uploadedUrl : formData.dlback || "",
          ...(side === "parivahanDoc" && { parivahanDoc: uploadedUrl }),
        };

        await fetch(API.ADMIN.ADMIN_VERIFY_DRIVING_LICENSE, {
          method: "POST",
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        // Refresh driver data
        if (onUpdated) onUpdated();
      } else {
        await axios.patch(
          `${API.ADMIN.DRIVER_LICENSEDETAILS}/${drivingLicense._id}`,
          { [side]: uploadedUrl },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }

      setFormData((prev) => ({
        ...prev,
        [side]: uploadedUrl,
      }));

      // Refresh driver data
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error("Upload error:", err);
      alert(side === "parivahanDoc" ? "Failed to upload Parivahan Doc." : "Failed to upload DL image.");
    } finally {
      setUploadingSide(null);
    }
  };

  const handleSaveField = async (field) => {

    if (field === "dateOfIssue" || field === "dateOfExpire") {
      const issueDate = parseFlexibleDate(formData.dateOfIssue);
      const expireDate = parseFlexibleDate(formData.dateOfExpire);
      if (issueDate && expireDate && expireDate <= issueDate) {
        alert("Date of Expiry must be after Date of Issue.");
        return;
      }
    }

    if (field === "dlNumber") {
      const trimmed = (formData.dlNumber || "").trim();
      const currentTrimmed = (drivingLicense?.dlNumber || "").trim();
      if (trimmed && trimmed !== currentTrimmed) {
        try {
          const alreadyUsed = await isDlNumberAlreadyUsed(trimmed);
          if (alreadyUsed) {
            setDlNumberError(DL_DUPLICATE_MESSAGE);
            return;
          }
        } catch (checkErr) {
          console.error("DL duplicate check error:", checkErr);
          setDlNumberError("Failed to validate driving license number");
          return;
        }
      }
      setDlNumberError("");
    }

    setIsSaving(true);
    try {
      const payload = {};
      if (field === "status") {
        payload.status = formData.status;
        if (formData.status === "Rejected" || formData.status === "Revisit") {
          payload.statusReason = formData.statusReason;
        }
      } else {
        payload[field] = formData[field];
      }

      // If DL record doesn't exist, create it
      if (!drivingLicense || !drivingLicense._id) {
        const createPayload = {
          ownerId: driverId,
          dlNumber: formData.dlNumber || "",
          dateOfBirth: formData.dateOfBirth || "",
          dateOfIssue: formData.dateOfIssue || "",
          dateOfExpire: formData.dateOfExpire || "",
          dlfront: formData.dlfront || "",
          dlback: formData.dlback || "",
          ...(formData.parivahanDoc && { parivahanDoc: formData.parivahanDoc }),
          ...payload,
        };

        const createResponse = await fetch(API.ADMIN.ADMIN_VERIFY_DRIVING_LICENSE, {
          method: "POST",
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(createPayload),
        });

        let createResult = null;
        try {
          createResult = await createResponse.json();
        } catch (parseErr) {
          createResult = null;
        }

        if (
          field === "dlNumber" &&
          (!createResponse.ok ||
            createResult?.success === false ||
            isDuplicateDlMessage(createResult?.message || createResult?.error))
        ) {
          if (
            isDuplicateDlMessage(createResult?.message || createResult?.error)
          ) {
            setDlNumberError(DL_DUPLICATE_MESSAGE);
          } else {
            setDlNumberError(
              createResult?.message ||
                createResult?.error ||
                "Failed to update field"
            );
          }
          return;
        }

        if (!createResponse.ok) {
          throw new Error(createResult?.message || createResult?.error || "Failed to update field");
        }
      } else {
        await axios.patch(
          `${API.ADMIN.DRIVER_LICENSEDETAILS}/${drivingLicense._id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      setEditField("");
      if (field === "dlNumber") {
        setDlNumberError("");
      }
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error("Update error:", err);
      const apiMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message;
      if (field === "dlNumber") {
        if (isDuplicateDlMessage(apiMessage)) {
          setDlNumberError(DL_DUPLICATE_MESSAGE);
        } else {
          setDlNumberError(apiMessage || "Failed to update field");
        }
      } else {
        alert("Failed to update field");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const renderEditableTextField = (label, fieldName) => {
    const isDateField = ["dateOfBirth", "dateOfIssue", "dateOfExpire"].includes(
      fieldName
    );
    return (
      <div>
        <strong>{label}:</strong>{" "}
        {editField === fieldName ? (
          <span className="inline-flex flex-col">
            <span className="flex gap-2 items-center">
            {isDateField ? (
              <DatePicker
                selected={parseFlexibleDate(formData[fieldName])}
                onChange={(date) => handleDateChange(date, fieldName)}
                dateFormat="dd/MM/yyyy"
                className="border px-2 py-1 rounded text-sm"
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={100}
                placeholderText="Select date"
                maxDate={fieldName === "dateOfBirth" ? new Date() : null}
                disabled={fieldName === "dateOfBirth" ? false : false}
              />
            ) : (
              <input
                type="text"
                name={fieldName}
                value={formData[fieldName]}
                onChange={handleChange}
                className="border px-2 py-1 rounded text-sm"
              />
            )}
            {isSaving ? (
              <FaSpinner className="animate-spin text-gray-500" />
            ) : (
              <>
                <FaCheck
                  className="text-green-600 cursor-pointer"
                  onClick={() => handleSaveField(fieldName)}
                />
                <FaTimes
                  className="text-red-600 cursor-pointer"
                  onClick={() => {
                    setFormData((p) => ({
                      ...p,
                      [fieldName]: drivingLicense[fieldName] || "",
                    }));
                    if (fieldName === "dlNumber") {
                      setDlNumberError("");
                    }
                    setEditField("");
                  }}
                />
              </>
            )}
            </span>
            {fieldName === "dlNumber" && dlNumberError && (
              <p className="text-red-500 text-sm mt-1">{dlNumberError}</p>
            )}
          </span>
        ) : (
          <span className="flex items-center gap-2">
            {isDateField
              ? formatDisplayDate(formData[fieldName])
              : formData[fieldName] || (showIfEmpty && detailsEditable ? "Click to add" : "N/A")}
            {detailsEditable && (
              <FaEdit
                className="text-blue-600 cursor-pointer"
                onClick={() => {
                  if (fieldName === "dlNumber") {
                    setDlNumberError("");
                  }
                  setEditField(fieldName);
                }}
              />
            )}
          </span>
        )}
      </div>
    );
  };

  return (
    <section className="p-6 bg-white rounded shadow mb-6">
      <div className="flex items-center gap-4 mb-6">
        <FaCar className="text-blue-600 text-2xl" />
        <h2 className="text-2xl font-semibold text-gray-800">
          Driving License Details
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {renderEditableTextField("Driving License Number", "dlNumber")}
        {renderEditableTextField("Date of Birth", "dateOfBirth")}
        {renderEditableTextField("Date of Issue", "dateOfIssue")}
        {renderEditableTextField("Date of Expiry", "dateOfExpire")}

        {/* Always show DL Images section when drivingLicense exists or showIfEmpty is true */}
        {/* This handles cases where dlfront/dlback are empty strings "" */}
        {(showIfEmpty || drivingLicense) && (
          <div className="md:col-span-2">
            <strong className="block mb-2">DL Images:</strong>
            <div className="flex gap-8 flex-wrap md:flex-nowrap">
              {["dlfront", "dlback"].map((side) => (
                <div key={side} className="flex flex-col items-start">
                  <span className="font-medium mb-1 capitalize">
                    {side === "dlfront" ? "Front" : "Back"}
                  </span>
                  <DocumentImage
                    imageUrl={formData[side]}
                    imageName={`Driving License ${
                      side === "dlfront" ? "Front" : "Back"
                    }`}
                    alt={`DL ${side}`}
                  >
                    {detailsEditable && (
                      <label className="mt-2 cursor-pointer flex gap-2 items-center text-sm text-blue-600 mt-8">
                        {uploadingSide === side ? (
                          <FaSpinner className="animate-spin" />
                        ) : (
                          <>
                            <FaUpload />
                            {formData[side] ? "Change" : "Upload"}
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileChange(e, side)}
                          disabled={uploadingSide}
                        />
                      </label>
                    )}
                  </DocumentImage>
                </div>
              ))}
              {/* Parivahan Doc: image or PDF only */}
              <div className="flex flex-col items-start">
                <span className="font-medium mb-1">Parivahan Doc</span>
                {(() => {
                  const url = formData.parivahanDoc;
                  const isPdf = url && (url.toLowerCase().includes(".pdf") || url.toLowerCase().includes("application/pdf"));
                  if (!url) {
                    return (
                      <div className="relative">
                        <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center w-72 h-44">
                          <span className="text-gray-500 text-sm mb-2">No document available</span>
                          {detailsEditable && (
                            <label className="mt-2 cursor-pointer flex gap-2 items-center text-sm text-blue-600">
                              {uploadingSide === "parivahanDoc" ? (
                                <FaSpinner className="animate-spin" />
                              ) : (
                                <>
                                  <FaUpload />
                                  Upload
                                </>
                              )}
                              <input
                                type="file"
                                accept="image/*,application/pdf"
                                className="hidden"
                                onChange={(e) => handleFileChange(e, "parivahanDoc")}
                                disabled={uploadingSide}
                              />
                            </label>
                          )}
                        </div>
                      </div>
                    );
                  }
                  if (isPdf) {
                    return (
                      <div className="relative">
                        <div className="bg-gray-100 border rounded-lg flex flex-col items-center justify-center w-72 h-44">
                          <span className="text-gray-600 text-sm mb-2">PDF document</span>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 text-sm underline"
                          >
                            View Parivahan Doc
                          </a>
                          {detailsEditable && (
                            <label className="mt-2 cursor-pointer flex gap-2 items-center text-sm text-blue-600">
                              {uploadingSide === "parivahanDoc" ? (
                                <FaSpinner className="animate-spin" />
                              ) : (
                                <>
                                  <FaUpload />
                                  Change
                                </>
                              )}
                              <input
                                type="file"
                                accept="image/*,application/pdf"
                                className="hidden"
                                onChange={(e) => handleFileChange(e, "parivahanDoc")}
                                disabled={uploadingSide}
                              />
                            </label>
                          )}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <DocumentImage
                      imageUrl={url}
                      imageName="Parivahan Doc"
                      alt="Parivahan Doc"
                    >
                      {detailsEditable && (
                        <label className="mt-2 cursor-pointer flex gap-2 items-center text-sm text-blue-600 mt-8">
                          {uploadingSide === "parivahanDoc" ? (
                            <FaSpinner className="animate-spin" />
                          ) : (
                            <>
                              <FaUpload />
                              Change
                            </>
                          )}
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            className="hidden"
                            onChange={(e) => handleFileChange(e, "parivahanDoc")}
                            disabled={uploadingSide}
                          />
                        </label>
                      )}
                    </DocumentImage>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>

      <DocumentStatusEditor
        statusForm={formData}
        setStatusForm={setFormData}
        isEditing={editField === "status"}
        onEdit={() => setEditField("status")}
        onCancel={() => {
          setFormData((prev) => ({
            ...prev,
            status: drivingLicense.status,
            statusReason: drivingLicense.statusReason,
          }));
          setEditField("");
        }}
        onSave={() => handleSaveField("status")}
        editable={statusEditable}
        userRole={currentUserRole}
      />
    </section>
  );
};

export default DrivingLicenseDetails;
